import React from "react"
import { sum } from "lodash"

import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
} from "~/components/reusable/Table"
import { api } from "~/utils/api"
import { formatDecimalToPercent, formatNumber, roundForecast } from "~/utils/formatting"
import { filterDefined, sumBy, groupBy } from "~/utils/tsUtils"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { formatName } from "~/utils/namingUtils"
import { Card, CardContent, CardHeader, CardTitle } from "~/components/reusable/Card"

function StorageLocationCard({ variants }: { variants: IVariantSummary[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Storage locations</CardTitle>
      </CardHeader>
      <CardContent className="max-h-[300px] overflow-auto">
        <StorageLocationTable variants={variants} />
      </CardContent>
    </Card>
  )
}

function StorageLocationTable({ variants }: { variants: IVariantSummary[] }) {
  const { data: locations } = api.storageLocation.get.useQuery()
  const isOneVariant = variants.length === 1

  const items = filterDefined(variants.flatMap((v) => v.storageLocationData))
  const rowData = Object.entries(groupBy(items, "locationId")).map(([locationId, items]) => {
    const location = locations?.find((l) => l.id === locationId)
    const locationName = location?.name || locationId
    const locationForecasts = sumBy(items, "thirteenWeeksForecast")
    const availability =
      sum(items.map((i) => i.thirteenWeeksForecast * i.availability)) / locationForecasts
    let weeksOnhHand = (sumBy(items, "stockLevel") / sumBy(items, "thirteenWeeksForecast")) * 13
    if (weeksOnhHand > 26) {
      weeksOnhHand = (sumBy(items, "stockLevel") / sumBy(items, "nextYearsForecast")) * 52
    }

    return {
      locationId,
      lastYearsSales: sumBy(items, "lastYearsSales"),
      weeksOnHand: sumBy(items, "thirteenWeeksForecast") ? weeksOnhHand : undefined,
      locationName,
      locationForecasts: sum(items.map((i) => i.nextYearsForecast)),
      availability,
      items,
      stockLevel: sumBy(items, "stockLevel"),
      minimumStockLevel: sumBy(items, "minimumStockLevel"),
      stockoutSkus: new Set(
        items
          .filter(
            (i) =>
              i.lastYearsSales > 0 || isOneVariant || (i.minimumStockLevel || i.stockLevel) > 0,
          )
          .flatMap(
            (i) =>
              i.skuData
                ?.filter((sku) => (sku.quantity ?? 0) <= 0)
                .filter((sku) => sku.weight + (sku.minimumStockLevel ?? 0) > 0)
                .map((sku) => sku.skuId) || [],
          ),
      ).size,
      activeSkus: new Set(
        items
          .filter(
            (i) =>
              i.lastYearsSales > 0 || isOneVariant || (i.minimumStockLevel || i.stockLevel) > 0,
          )
          .flatMap(
            (i) =>
              i.skuData
                ?.filter((sku) => sku.weight + sku.quantity + (sku.minimumStockLevel ?? 0) > 0)
                .map((sku) => sku.skuId) || [],
          ),
      ).size,
      missingStock: sum(
        items.flatMap(
          (i) =>
            i?.skuData?.map((sku) => Math.max((sku.minimumStockLevel ?? 0) - sku.quantity, 0)) ??
            [],
        ),
      ),
    }
  })
  const hasMinimumStockLevel = sumBy(items, "minimumStockLevel") > 0

  return (
    <div>
      {/* <div className="flex">
        Comparison weeks
        <Slider
          defaultValue={[6]}
          min={1}
          max={13} // WE ONLY HAVE A YEAR AND 13 WEEKS OF DATA, SEE THE get_raw_sales-FUNCTION IN read_sales.py
          step={1}
          value={[weeks]}
          onValueChange={(value) => setWeeks(value[0] ?? 6)}
        />
      </div> */}
      <SimpleTable>
        <SimpleTableHeader>
          <SimpleTableRow>
            <SimpleTableHead>Location</SimpleTableHead>
            <SimpleTableHead title={`${formatName("sale", true, true)} (Last 52 weeks)`}>
              {formatName("sale", true, true)}
            </SimpleTableHead>
            <SimpleTableHead title="Demand (Next year)">Demand</SimpleTableHead>
            <SimpleTableHead>Stock level</SimpleTableHead>
            <SimpleTableHead>Availability %</SimpleTableHead>
            <SimpleTableHead># Stock skus</SimpleTableHead>
            <SimpleTableHead>Weeks on hand</SimpleTableHead>
            {hasMinimumStockLevel ? <SimpleTableHead>Minimum stock</SimpleTableHead> : null}
            {/* <TableHead>Last {weeks} sales</TableHead> // If this is ever commented in, use formatName
            <TableHead>Last {weeks} revenue</TableHead> */}
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {rowData.map((row) => (
            <SimpleTableRow key={row.locationId}>
              <SimpleTableCell>{row.locationName}</SimpleTableCell>
              <SimpleTableCell>{formatNumber(row.lastYearsSales, 2)}</SimpleTableCell>
              <SimpleTableCell>
                {formatNumber(roundForecast(row.locationForecasts))}
              </SimpleTableCell>
              <SimpleTableCell>{formatNumber(row.stockLevel, 2)}</SimpleTableCell>
              <SimpleTableCell>{formatDecimalToPercent(row.availability)}</SimpleTableCell>
              <SimpleTableCell>{`${formatNumber(row.activeSkus - row.stockoutSkus, 2)} / ${formatNumber(row.activeSkus, 2)}`}</SimpleTableCell>
              <SimpleTableCell>
                {row.weeksOnHand !== undefined ? Math.round(row.weeksOnHand) : " - "}
              </SimpleTableCell>
              {hasMinimumStockLevel ? (
                <SimpleTableCell>{`${formatNumber(row.minimumStockLevel - row.missingStock, 2)} / ${formatNumber(row.minimumStockLevel, 2)}`}</SimpleTableCell>
              ) : null}
              {/* <SimpleTableCell>
                <ComparisonField
                  value={comparisonData?.find((d) => d.locationId === row.locationId)?.quantityNow}
                  valueBefore={
                    comparisonData?.find((d) => d.locationId === row.locationId)?.quantityAYearAgo
                  }
                  format={(a) => formatNumberWithSuffix(a, 2)}
                />
              </SimpleTableCell>
              <SimpleTableCell>
                <ComparisonField
                  value={comparisonData?.find((d) => d.locationId === row.locationId)?.revenueNow}
                  valueBefore={
                    comparisonData?.find((d) => d.locationId === row.locationId)?.revenueAYearAgo
                  }
                  format={(a) => formatNumberWithSuffix(a, 2)}
                />
              </SimpleTableCell> */}
            </SimpleTableRow>
          ))}
        </SimpleTableBody>
      </SimpleTable>
    </div>
  )
}

export { StorageLocationCard, StorageLocationTable }
